import isEqual from 'lodash/isEqual';
import pick from 'lodash/pick';

import {
	isAIFCAction,
	isCreatableAction,
} from '@atlassian/conversation-assistant-action-helpers/utils';
import { isJiraPreviewableAction } from '@atlassian/conversation-assistant-action-helpers/utils/jira-actions';
import { ASK_QUESTION_KEY } from '@atlassian/conversation-assistant-message-actions-api/constants';
import type { MessageAction } from '@atlassian/conversation-assistant-service-api/types/message-actions';
import {
	isStoreAssistant,
	type StoreAssistant,
	type StoreChatItem,
} from '@atlassian/conversation-assistant-store/controllers/chat/types';

export type PreviewStatus = 'preparing' | 'streaming' | 'complete';

export function findMessageById(
	messages: StoreChatItem[],
	messageId: string,
): StoreAssistant | undefined {
	return messages.find(
		(msg): msg is StoreAssistant =>
			isStoreAssistant(msg) && msg.content.message.message.id === messageId,
	);
}

export function findLastMessageWithCreateAction(
	messages: StoreChatItem[],
): StoreAssistant | undefined {
	for (let i = messages.length - 1; i >= 0; i--) {
		const msg = messages[i];
		if (
			isStoreAssistant(msg) &&
			(msg.content.message.message.actions || []).some(
				(action) => isCreatableAction(action) || isJiraPreviewableAction(action),
			)
		) {
			return msg;
		}
	}
	return undefined;
}

export function findLastMessageWithAIFCAction(
	messages: StoreChatItem[],
): StoreAssistant | undefined {
	for (let i = messages.length - 1; i >= 0; i--) {
		const msg = messages[i];
		if (isStoreAssistant(msg) && (msg.content.message.message.actions || []).some(isAIFCAction)) {
			return msg;
		}
	}
	return undefined;
}

export function findLastAssistantMessage(messages: StoreChatItem[]): StoreAssistant | undefined {
	for (let i = messages.length - 1; i >= 0; i--) {
		const msg = messages[i];
		if (isStoreAssistant(msg)) {
			return msg;
		}
	}
	return undefined;
}

/**
 * Returns true if the most recent assistant message that has actions contains an ask_question action.
 * Scans backwards through messages and stops at the first assistant message with actions — so a
 * later create or AIFC action will naturally return false, even if an ask_question exists earlier
 * in the conversation.
 */
export function isLatestActionMessageAskQuestion(messages: StoreChatItem[]): boolean {
	for (let i = messages.length - 1; i >= 0; i--) {
		const msg = messages[i];
		if (isStoreAssistant(msg) && msg.content.message.message.actions?.length) {
			return msg.content.message.message.actions.some((action) => action.key === ASK_QUESTION_KEY);
		}
	}
	return false;
}

/**
 * Returns a shallow clone of `message` with the action matching `invocationId` moved to the front of
 * its actions array, so code that reads the first action operates on the selected one. No-op when
 * there is no invocationId, there are fewer than two actions, or the action is already first.
 */
export function withSelectedActionFirst(
	message: StoreAssistant | undefined,
	invocationId?: string | null,
): StoreAssistant | undefined {
	if (!message || !invocationId) {
		return message;
	}
	const actions = message.content.message.message.actions;
	if (!actions || actions.length < 2) {
		return message;
	}
	const index = actions.findIndex((action) => action.invocationId === invocationId);
	if (index <= 0) {
		return message;
	}
	const reorderedActions = [
		actions[index],
		...actions.slice(0, index),
		...actions.slice(index + 1),
	];
	// Clone only the path down to `actions`; everything else (notably the streamed action `data`) is
	// shared by reference so we don't deep-copy potentially large ADF on every card switch.
	const assistantMessage = { ...message.content.message.message, actions: reorderedActions };
	return {
		...message,
		content: {
			...message.content,
			message: { ...message.content.message, message: assistantMessage },
		},
	};
}

// Resolves the creatable action a preview should use: the one matching `invocationId` when given
// (a message can hold several creatable actions), otherwise the first creatable action.
function findPreviewAction(
	message: StoreAssistant | undefined,
	invocationId?: string | null,
): MessageAction | undefined {
	const actions = message?.content?.message?.message?.actions;
	if (invocationId) {
		const byInvocation = actions?.find(
			(action) => action.invocationId === invocationId && isCreatableAction(action),
		);
		if (byInvocation) {
			return byInvocation;
		}
	}
	return actions?.find(isCreatableAction);
}

export function extractContentId(
	message: StoreAssistant,
	invocationId?: string | null,
): string | undefined {
	const action = findPreviewAction(message, invocationId);
	const contentId = (action?.data as { contentId: string | number } | undefined)?.contentId;
	// always make it a string since the final response sometimes returns a number and re-renders the iframe.
	return contentId === undefined ? undefined : String(contentId);
}

export function getFirstMessageAction(
	message: StoreAssistant | undefined,
): MessageAction | undefined {
	return message?.content?.message?.message?.actions?.[0];
}

export function extractTitle(
	message: StoreAssistant,
	invocationId?: string | null,
): string | undefined {
	const action = findPreviewAction(message, invocationId);
	return (action?.data as { title: string } | undefined)?.title;
}

/**
 * Finds the current status of an AIFC action.
 * This function helps us determine the status and we use it to show the preview and other things like loading.
 * We do not want to show the preview until the the stream gives us streaming data or the streaming is complete.
 */
export function getAIFCActionStatus(
	message: StoreAssistant | undefined,
	invocationId?: string | null,
): PreviewStatus | undefined {
	if (!message) {
		return undefined;
	}

	const actions = message?.content?.message?.message?.actions;
	const aifcAction = invocationId
		? actions?.find((action) => action.invocationId === invocationId && isAIFCAction(action))
		: actions?.find(isAIFCAction);

	if (aifcAction) {
		const status = aifcAction?.status?.toLowerCase();
		if (status === 'preparing' || status === 'streaming') {
			return status as PreviewStatus;
		}

		// 'Planned' is the same as a final response. If we see it, assume streaming is complete.
		if (
			status === 'planned' ||
			status === 'finished' ||
			status === 'cancelled' ||
			message.content.streamingComplete
		) {
			return 'complete';
		}
	}

	return undefined;
}

function getStableMessageParts(message: StoreAssistant) {
	const action = message.content.message.message.actions?.find(isCreatableAction);
	const actionData = action?.data;

	// We just pick the parts we care about directly from the message
	// We avoid ADF since that is streamed and changes all the time.
	return {
		messageId: message.content.message.message.id,
		streamingComplete: message.content.streamingComplete,
		action: actionData ? pick(actionData, ['contentId', 'title', 'mediaFileId']) : undefined,
		status: action?.status,
		traceId: message.traceId,
	};
}

export function areMessagesEqual(a?: StoreAssistant, b?: StoreAssistant): boolean {
	if (a === b) {
		return true;
	}
	if (!a || !b) {
		return false;
	}

	return isEqual(getStableMessageParts(a), getStableMessageParts(b));
}
